import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useBrain } from "@/contexts/ui/brain";
import { TUTOR_PROMPT_CATALOG, TutorPromptGroup } from "@/lib/learn/tutorPromptCatalog";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const MASCOT_SRC = "/genie-mascots/role/support.png";

const TutorTabPanel = () => {
  const navigate = useNavigate();
  const { openBrain } = useBrain();
  const [searchTerm, setSearchTerm] = useState("");

  const filteredGroups: TutorPromptGroup[] = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return TUTOR_PROMPT_CATALOG;
    return TUTOR_PROMPT_CATALOG.map((group) => ({
      ...group,
      prompts: group.prompts.filter((p) => p.toLowerCase().includes(term)),
    })).filter((g) => g.prompts.length > 0);
  }, [searchTerm]);

  return (
    <div className="space-y-8">
      {/* Hero panel */}
      <div className="relative overflow-hidden rounded-2xl border-2 border-genie-primary/20 bg-gradient-to-br from-genie-primary/5 via-white to-genie-secondary/5 p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <img
            src={MASCOT_SRC}
            alt="Help Genie mascot"
            className="w-24 h-24 sm:w-32 sm:h-32 object-contain flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-genie-text">Your Help Genie Tutor</h2>
            <p className="text-sm sm:text-base text-gray-600 mt-2 max-w-2xl">
              A dedicated Genie who knows the whole platform inside out. Ask anything — setup,
              troubleshooting, best practice. It's also the same Genie behind the{" "}
              <span className="font-medium text-genie-primary">Your Genie</span> sidebar, so you can
              pick the conversation up anywhere.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <Button
                onClick={() => navigate("/learn/tutor")}
                className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200"
              >
                Open full Tutor
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => openBrain()}
                className="border-genie-primary/30 text-genie-primary hover:bg-genie-primary/5"
              >
                Open in the sidebar
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Catalog */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
          <div>
            <h3 className="text-lg font-semibold text-genie-text">Quick starters</h3>
            <p className="text-sm text-gray-600">
              Tap any of these to open the Tutor with the question pre-loaded. Grouped by area of
              the app so you can scan to what you need.
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-white border-gray-200 focus-visible:ring-genie-primary"
            />
          </div>
        </div>

        {filteredGroups.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-gray-200 bg-white p-12 text-center">
            <p className="text-sm text-gray-600 mb-2">
              No quick starters match "{searchTerm}".
            </p>
            <button
              onClick={() => openBrain(searchTerm)}
              className="text-sm font-medium text-genie-primary hover:underline"
            >
              Ask the Tutor: "{searchTerm}" →
            </button>
          </div>
        ) : (
          <div className="space-y-7">
            {filteredGroups.map((group) => (
              <section key={group.key}>
                <div className="flex items-center gap-3 mb-3">
                  <GenieIcon
                    name={group.genieIcon}
                    size={36}
                    className="h-9 w-9 flex-shrink-0"
                  />
                  <div>
                    <h4 className="text-base font-semibold text-genie-text">{group.title}</h4>
                    {group.description && (
                      <p className="text-xs text-gray-500">{group.description}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {group.prompts.map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => openBrain(prompt)}
                      className="group text-left p-3 rounded-xl bg-white border border-gray-200 hover:border-genie-primary/40 hover:shadow-sm transition-all flex items-start gap-2.5"
                    >
                      <GenieIcon name="chat" size={20} className="mt-0.5 opacity-90" />
                      <span className="text-sm text-gray-700 group-hover:text-genie-text leading-snug">
                        {prompt}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TutorTabPanel;
